import { FocusKeyManager } from '@angular/cdk/a11y';
import { NgTemplateOutlet, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { inject, ElementRef, EventEmitter, booleanAttribute, Component, ChangeDetectionStrategy, Input, Output, Directive, TemplateRef, ContentChild, ViewChild, DestroyRef, ChangeDetectorRef, numberAttribute, ContentChildren, ViewChildren, NgModule } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UxaDirectionality } from '@ux-aspects-universal/angular/i18n';
import { mixinVariant } from '@ux-aspects-universal/angular/mixins';
import { Subject, Subscription, merge } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { UxaFocusIndicator, UxaA11yModule } from '@ux-aspects-universal/angular/a11y';
const _c0 = ["uxa-tab-button", ""];
const _c1 = ["*"];
const _c2 = ["tabContent"];
function UxaTabComponent_ng_template_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function UxaTabGroupComponent_For_2_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtext(0);
  }
  if (rf & 2) {
    const tab_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵtextInterpolate1(" ", tab_r4.label, " ");
  }
}
function UxaTabGroupComponent_For_2_Conditional_2_ng_template_0_Template(rf, ctx) {}
function UxaTabGroupComponent_For_2_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, UxaTabGroupComponent_For_2_Conditional_2_ng_template_0_Template, 0, 0, "ng-template", 4);
  }
  if (rf & 2) {
    const tab_r4 = i0.ɵɵnextContext().$implicit;
    i0.ɵɵproperty("ngTemplateOutlet", tab_r4._customLabelRef);
  }
}
function UxaTabGroupComponent_For_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 3);
    i0.ɵɵlistener("keydown", function UxaTabGroupComponent_For_2_Template_button_keydown_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1._onKeydown($event));
    })("selected", function UxaTabGroupComponent_For_2_Template_button_selected_0_listener() {
      const ɵ$index_3_r3 = i0.ɵɵrestoreView(_r1).$index;
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1.select(ɵ$index_3_r3));
    });
    i0.ɵɵtemplate(1, UxaTabGroupComponent_For_2_Conditional_1_Template, 1, 1)(2, UxaTabGroupComponent_For_2_Conditional_2_Template, 1, 1, null, 4);
    i0.ɵɵelement(3, "div", 5);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const tab_r4 = ctx.$implicit;
    i0.ɵɵproperty("id", tab_r4.id)("active", tab_r4.active)("disabled", tab_r4.disabled);
    i0.ɵɵattribute("aria-label", tab_r4.ariaLabel)("aria-labelledby", tab_r4.ariaLabelledby);
    i0.ɵɵadvance();
    i0.ɵɵconditional(!tab_r4._customLabelRef ? 1 : 2);
  }
}
function UxaTabGroupComponent_For_4_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 6);
    i0.ɵɵelementContainer(1, 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const tab_r5 = ctx.$implicit;
    const ɵ$index_13_r6 = ctx.$index;
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵclassProp("uxa-selected", ɵ$index_13_r6 === ctx_r1.activeIndex);
    i0.ɵɵadvance();
    i0.ɵɵproperty("ngTemplateOutlet", tab_r5.content);
  }
}
class UxaTabButtonComponent {
  constructor() {
    this._elementRef = inject(ElementRef);
    this._focusIndicator = inject(UxaFocusIndicator);
    /** Get the tab active state */
    this.active = false;
    /** Required for focusKeyManager to know if this item can be focused */
    this.disabled = false;
    /** Emit when the tab is selected */
    this.selected = new EventEmitter();
  }
  ngOnInit() {
    this._focusIndicator.monitor(this._elementRef.nativeElement);
  }
  ngOnDestroy() {
    this._focusIndicator.stopMonitoring(this._elementRef.nativeElement);
  }
  focus() {
    this._focusIndicator.focusVia(this._elementRef.nativeElement, 'keyboard');
    this.selected.emit();
  }
  static {
    this.ɵfac = function UxaTabButtonComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaTabButtonComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaTabButtonComponent,
      selectors: [["", "uxa-tab-button", ""]],
      hostAttrs: ["role", "tab", 1, "uxa-tab"],
      hostVars: 8,
      hostBindings: function UxaTabButtonComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("click", function UxaTabButtonComponent_click_HostBindingHandler() {
            return ctx.selected.emit();
          });
        }
        if (rf & 2) {
          i0.ɵɵhostProperty("id", ctx.id + "-button")("tabindex", ctx.active ? 0 : -1)("disabled", ctx.disabled);
          i0.ɵɵattribute("aria-selected", ctx.active && !ctx.disabled);
          i0.ɵɵclassProp("uxa-selected", ctx.active && !ctx.disabled)("uxa-disabled", ctx.disabled);
        }
      },
      inputs: {
        id: "id",
        active: [2, "active", "active", booleanAttribute],
        disabled: [2, "disabled", "disabled", booleanAttribute]
      },
      outputs: {
        selected: "selected"
      },
      attrs: _c0,
      ngContentSelectors: _c1,
      decls: 1,
      vars: 0,
      template: function UxaTabButtonComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵprojection(0);
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaTabButtonComponent, [{
    type: Component,
    args: [{
      selector: '[uxa-tab-button]',
      changeDetection: ChangeDetectionStrategy.OnPush,
      host: {
        role: 'tab',
        class: 'uxa-tab',
        '[id]': 'id + "-button"',
        '[tabindex]': 'active ? 0 : -1',
        '[class.uxa-selected]': 'active && !disabled',
        '[class.uxa-disabled]': 'disabled',
        '[attr.aria-selected]': 'active && !disabled',
        '[disabled]': 'disabled',
        '(click)': 'selected.emit()'
      },
      standalone: true,
      template: "<ng-content />\n"
    }]
  }], null, {
    id: [{
      type: Input,
      args: [{
        required: true
      }]
    }],
    active: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    disabled: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    selected: [{
      type: Output
    }]
  });
})();
class UxaTabLabelDirective {
  static {
    this.ɵfac = function UxaTabLabelDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaTabLabelDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: UxaTabLabelDirective,
      selectors: [["", "uxaTabLabel", ""]]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaTabLabelDirective, [{
    type: Directive,
    args: [{
      selector: '[uxaTabLabel]',
      standalone: true
    }]
  }], null, null);
})();

/** A unique id for the tabs */
let uniqueId = 0;
/** Apply any mixins to the tab component */
class UxaTabComponent {
  constructor() {
    // @internal Active state of the tab, for use in the template
    this.active = false;
    /** A unique identifier for the tab. */
    this.id = `uxa-tab-${uniqueId++}`;
    /** Whether the tab id disabled. */
    this.disabled = false;
    /** @internal Store a custom tab header templateRef */
    this._customLabelRef = null;
    /** @internal Access the tab content */
    this.content = null;
    /** @internal Emits whenever the disabled state changes for the tab. */
    this._disabledStateChange$ = new Subject();
  }
  ngOnChanges(changes) {
    if (changes['disabled'] && changes['disabled'].previousValue !== changes['disabled'].currentValue) {
      this._disabledStateChange$.next();
    }
  }
  ngOnDestroy() {
    this._disabledStateChange$.complete();
  }
  static {
    this.ɵfac = function UxaTabComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaTabComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaTabComponent,
      selectors: [["uxa-tab"]],
      contentQueries: function UxaTabComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, UxaTabLabelDirective, 5, TemplateRef);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._customLabelRef = _t.first);
        }
      },
      viewQuery: function UxaTabComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c2, 7);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.content = _t.first);
        }
      },
      hostVars: 2,
      hostBindings: function UxaTabComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("aria-label", null)("aria-labelledby", null);
        }
      },
      inputs: {
        disabled: [2, "disabled", "disabled", booleanAttribute],
        ariaLabel: [0, "aria-label", "ariaLabel"],
        ariaLabelledby: [0, "aria-labelledby", "ariaLabelledby"],
        label: "label",
        id: "id"
      },
      features: [i0.ɵɵNgOnChangesFeature],
      ngContentSelectors: _c1,
      decls: 2,
      vars: 0,
      consts: [["tabContent", ""]],
      template: function UxaTabComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵtemplate(0, UxaTabComponent_ng_template_0_Template, 1, 0, "ng-template", null, 0, i0.ɵɵtemplateRefExtractor);
        }
      },
      encapsulation: 2,
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaTabComponent, [{
    type: Component,
    args: [{
      selector: 'uxa-tab',
      changeDetection: ChangeDetectionStrategy.OnPush,
      inputs: ['disabled'],
      host: {
        '[attr.aria-label]': 'null',
        '[attr.aria-labelledby]': 'null'
      },
      standalone: true,
      template: "<ng-template #tabContent>\n  <ng-content />\n</ng-template>\n"
    }]
  }], null, {
    ariaLabel: [{
      type: Input,
      args: ['aria-label']
    }],
    ariaLabelledby: [{
      type: Input,
      args: ['aria-labelledby']
    }],
    label: [{
      type: Input
    }],
    id: [{
      type: Input
    }],
    disabled: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    _customLabelRef: [{
      type: ContentChild,
      args: [UxaTabLabelDirective, {
        read: TemplateRef
      }]
    }],
    content: [{
      type: ViewChild,
      args: ['tabContent', {
        static: true
      }]
    }]
  });
})();

/** This class is required to apply the mixins to UxaTabComponent */
class UxaTabGroupBase {}
/** Apply any mixins to the tab component */
const UxaTabGroupMixinBase = mixinVariant(UxaTabGroupBase, 'secondary');
class UxaTabGroupComponent extends UxaTabGroupMixinBase {
  constructor() {
    super(...arguments);
    this._destroyRef = inject(DestroyRef);
    this._directionality = inject(UxaDirectionality);
    this._changeDetector = inject(ChangeDetectorRef);
    /** Define the orientation of the tab group */
    this.orientation = 'horizontal';
    /** Event emitted when the tab active index value changes */
    this.activeIndexChange = new EventEmitter();
    /** Event emitted with the ID of the selected tab. */
    this.activeIdChange = new EventEmitter();
    /** Subscription to tab state changes. */
    this._tabStateChangeSubscription = Subscription.EMPTY;
    /** @internal */
    this.trackByTab = (_, tab) => tab.id ?? tab.label ?? tab;
  }
  /** The size of the tab buttons. */
  set size(size) {
    this._size = size;
  }
  get size() {
    return this._size ?? (this.variant === 'primary' ? 'medium' : 'small');
  }
  /** The index of the active tab */
  set activeIndex(index) {
    if (this._tabs) {
      this.selectByIndex(index, false);
    } else {
      this._pendingActiveIndex = index;
    }
  }
  get activeIndex() {
    return this._tabs?.toArray().findIndex(tab => tab.id === this.activeId);
  }
  /** The id of the active tab */
  set activeId(id) {
    if (this._tabs) {
      this._activeId = id;
      this.selectById(id, false);
    } else {
      this._pendingActiveId = id;
    }
  }
  get activeId() {
    return this._activeId;
  }
  ngAfterViewInit() {
    this._keyManager = new FocusKeyManager(this._buttons).withHorizontalOrientation(this._directionality.dir).withHomeAndEnd();
    // make the first item initially tabbable
    this._keyManager.updateActiveItem(0);
    // listen for future changes to directionality
    this._directionality.dirChange$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(dir => this._keyManager?.withHorizontalOrientation(dir));
    // if the tabs change after the initial render then we need to run change detection as the
    // tab headers in the template will not have been updated and ensure there is a selected tab
    this._tabs?.changes.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.activeId ? this.select(this.activeId) : this.select(0);
      this._changeDetector.detectChanges();
    });
  }
  ngAfterContentInit() {
    // Subscribe to changes in the amount of tabs, in order to be
    // able to re-render the content as new tabs are added or removed.
    this._tabs?.changes.pipe(startWith(this._tabs), takeUntilDestroyed(this._destroyRef)).subscribe(() => this.subscribeToTabStateChanges());
    // select the tab at the specified active index
    if (this._pendingActiveId) {
      this.selectById(this._pendingActiveId, false);
      this._pendingActiveId = undefined;
    } else if (this._pendingActiveIndex) {
      this.selectByIndex(this._pendingActiveIndex, false);
      this._pendingActiveIndex = undefined;
    } else {
      this.selectByIndex(0);
    }
  }
  ngOnDestroy() {
    this._tabStateChangeSubscription.unsubscribe();
  }
  /** Gets called whenever disabled state and tab changes */
  subscribeToTabStateChanges() {
    this._tabStateChangeSubscription?.unsubscribe();
    this._tabStateChangeSubscription = merge(...this._tabs.map(tab => tab._disabledStateChange$)).subscribe(() => this._changeDetector.markForCheck());
  }
  select(tab) {
    if (typeof tab === 'number') {
      return this.selectByIndex(tab);
    }
    if (typeof tab === 'string') {
      return this.selectById(tab);
    }
  }
  /** Select the tab with the specified id */
  selectById(id, shouldEmit = true) {
    // if there are no tabs skip or all tabs are disabled
    if (!this._tabs || this._tabs.toArray().every(tab => tab.disabled)) {
      return;
    }
    // deactivate all tabs
    this._tabs?.forEach(tab => tab.active = false);
    // if there is no id then select the first tab
    if (!id) {
      return this.selectByIndex(0);
    }
    // check if the target tab exists and is not disabled
    const targetTab = this._tabs.find(tab => tab.id === id);
    // if the target tab does not exist select the first tabs
    if (!targetTab) {
      return this.selectByIndex(0);
    }
    // if the tab is disabled then select the next tab, if there is no next tab
    // the select function will default to the first non-disabled tab
    if (targetTab.disabled) {
      // if no tab at this index exists this will select the first enabled tab
      return this.selectByIndex(this._tabs.toArray().findIndex(tab => tab === targetTab) + 1);
    }
    // activate the tab active item
    this._keyManager?.updateActiveItem(this._tabs.toArray().findIndex(tab => tab === targetTab));
    // activate the tab the user has selected.
    targetTab.active = true;
    // if the target tab is already active then skip
    if (targetTab.id === this._activeId) {
      return;
    }
    // store the active id
    this._activeId = targetTab.id;
    this._changeDetector.detectChanges();
    // avoid expression changed error
    Promise.resolve().then(() => {
      // emit the id of the selected tab and the active index
      if (shouldEmit) {
        this.activeIdChange.emit(targetTab.id);
        this.activeIndexChange.emit(this.activeIndex);
      }
    });
  }
  /** Select the tab at the specified index */
  selectByIndex(index, shouldEmit = true) {
    return this.selectById(this._tabs?.get(index)?.id, shouldEmit);
  }
  /** @internal Handle keyboard events for tab selection and navigating between tabs */
  _onKeydown(event) {
    this._keyManager?.onKeydown(event);
  }
  static {
    this.ɵfac = /* @__PURE__ */(() => {
      let ɵUxaTabGroupComponent_BaseFactory;
      return function UxaTabGroupComponent_Factory(__ngFactoryType__) {
        return (ɵUxaTabGroupComponent_BaseFactory || (ɵUxaTabGroupComponent_BaseFactory = i0.ɵɵgetInheritedFactory(UxaTabGroupComponent)))(__ngFactoryType__ || UxaTabGroupComponent);
      };
    })();
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: UxaTabGroupComponent,
      selectors: [["uxa-tab-group"]],
      contentQueries: function UxaTabGroupComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, UxaTabComponent, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._tabs = _t);
        }
      },
      viewQuery: function UxaTabGroupComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(UxaTabButtonComponent, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._buttons = _t);
        }
      },
      hostVars: 9,
      hostBindings: function UxaTabGroupComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵattribute("aria-label", null);
          i0.ɵɵclassProp("uxa-tab-group", true)("uxa-vertical", ctx.orientation === "vertical")("uxa-small", ctx.size === "small")("uxa-medium", ctx.size === "medium");
        }
      },
      inputs: {
        variant: "variant",
        size: "size",
        activeIndex: [2, "activeIndex", "activeIndex", numberAttribute],
        activeId: "activeId",
        orientation: "orientation",
        ariaLabel: [0, "aria-label", "ariaLabel"]
      },
      outputs: {
        activeIndexChange: "activeIndexChange",
        activeIdChange: "activeIdChange"
      },
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 5,
      vars: 1,
      consts: [["role", "tablist", 1, "uxa-tab-list"], ["type", "button", "uxa-tab-button", "", 3, "id", "active", "disabled"], ["role", "tabpanel", 1, "uxa-tab-panel", 3, "uxa-selected"], ["type", "button", "uxa-tab-button", "", 3, "keydown", "selected", "id", "active", "disabled"], [3, "ngTemplateOutlet"], ["role", "presentation", 1, "uxa-tab-indicator"], ["role", "tabpanel", 1, "uxa-tab-panel"]],
      template: function UxaTabGroupComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵelementStart(0, "div", 0);
          i0.ɵɵrepeaterCreate(1, UxaTabGroupComponent_For_2_Template, 4, 6, "button", 1, ctx.trackByTab, true);
          i0.ɵɵelementEnd();
          i0.ɵɵrepeaterCreate(3, UxaTabGroupComponent_For_4_Template, 2, 3, "div", 2, ctx.trackByTab, true);
        }
        if (rf & 2) {
          i0.ɵɵattribute("aria-orientation", ctx.orientation);
          i0.ɵɵadvance();
          i0.ɵɵrepeater(ctx._tabs);
          i0.ɵɵadvance(2);
          i0.ɵɵrepeater(ctx._tabs);
        }
      },
      dependencies: [UxaTabButtonComponent, NgTemplateOutlet],
      styles: [".uxa-tab-group[_ngcontent-%COMP%], .uxa-tab-group[_nghost-%COMP%]{display:flex;flex-direction:column;column-gap:var(--uxa-tab-group__column-gap);height:100%}.uxa-tab-group.uxa-secondary[_ngcontent-%COMP%], .uxa-tab-group.uxa-secondary[_nghost-%COMP%]{--uxa-tab-indicator__background-color: var(--uxa-tab-indicator__background-color--secondary);--uxa-tab-indicator__background-color--hover: var( --uxa-tab-indicator__background-color--secondary-hover );--uxa-tab-indicator__background-color--selected: var( --uxa-tab-indicator__background-color--secondary-selected );--uxa-tab-indicator__background-color--selected-hover: var( --uxa-tab-indicator__background-color--secondary-selected-hover );--uxa-tab-indicator__background-color--active: var( --uxa-tab-indicator__background-color--secondary-active );--uxa-tab-indicator__background-color--selected-active: var( --uxa-tab-indicator__background-color--secondary-selected-active )}.uxa-tab-group.uxa-small[_ngcontent-%COMP%], .uxa-tab-group.uxa-small[_nghost-%COMP%]{--uxa-tab-list__height: var(--uxa-tab-list__height--small);--uxa-tab__font-size: var(--uxa-tab__font-size--small)}.uxa-tab-group.uxa-medium[_ngcontent-%COMP%], .uxa-tab-group.uxa-medium[_nghost-%COMP%]{--uxa-tab-list__height: var(--uxa-tab-list__height--medium);--uxa-tab__font-size: var(--uxa-tab__font-size--medium)}.uxa-tab-group.uxa-vertical[_ngcontent-%COMP%], .uxa-tab-group.uxa-vertical[_nghost-%COMP%]{flex-direction:row;--uxa-tab__height: var(--uxa-tab__height--vertical);--uxa-tab__padding-inline: .75rem;--uxa-tab__padding-block: 0;--uxa-tab__justify-content: flex-start;--uxa-tab-list__flex-direction: column;--uxa-tab-list__height: auto;--uxa-tab-list__border-bottom-width: var(--uxa-tab-list__border-bottom-width--vertical);--uxa-tab-list__border-inline-end-width: var(--uxa-tab-list__border-inline-end-width--vertical);--uxa-tab-indicator__inset-inline-start: 0;--uxa-tab-indicator__width--selected: 3px;--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--vertical);--uxa-tab-indicator__transition: height .24s ease-out;--uxa-tab-indicator__translate: translate(0, -50%);--uxa-tab-indicator__top: 50%;--uxa-tab-indicator__border-radius: var(--uxa-tab-indicator__border-radius--vertical);--uxa-tab-indicator__height--hover: var(--uxa-tab-indicator__height--vertical-hover);--uxa-tab-indicator__width--hover: var(--uxa-tab-indicator__width--vertical-hover);--uxa-tab-indicator__width--selected-hover: var( --uxa-tab-indicator__width--vertical-selected-hover );--uxa-tab-indicator__height--active: var(--uxa-tab-indicator__height--vertical-active);--uxa-tab-indicator__width--active: var(--uxa-tab-indicator__width--vertical-active);--uxa-tab-indicator__width--selected-active: var( --uxa-tab-indicator__width--vertical-selected-active );--uxa-tab-indicator__height--selected-active: var( --uxa-tab-indicator__height--vertical-selected-active )}.uxa-tab-group.uxa-vertical[_ngcontent-%COMP%]   .uxa-tab.uxa-selected[_ngcontent-%COMP%], .uxa-tab-group.uxa-vertical[_nghost-%COMP%]   .uxa-tab.uxa-selected[_ngcontent-%COMP%]{--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--vertical-selected)}.uxa-tab-list[_ngcontent-%COMP%]{flex:none;display:flex;flex-direction:var(--uxa-tab-list__flex-direction, row);column-gap:var(--uxa-tab-list__column-gap);height:var(--uxa-tab-list__height);border-top-width:var(--uxa-tab-list__border-top-width);border-bottom-width:var(--uxa-tab-list__border-bottom-width);border-inline-start-width:var(--uxa-tab-list__border-inline-start-width);border-inline-end-width:var(--uxa-tab-list__border-inline-end-width);border-style:var(--uxa-tab-list__border-style);border-color:var(--uxa-tab-list__border-color)}.uxa-tab[_ngcontent-%COMP%]{position:relative;display:inline-flex;justify-content:var(--uxa-tab__justify-content);align-items:center;height:var(--uxa-tab__height);text-decoration:none;flex:none;background-color:var(--uxa-tab__background-color);margin-inline:var(--uxa-tab__margin-inline);border:var(--uxa-tab__border);padding-inline:var(--uxa-tab__padding-inline);padding-block:var(--uxa-tab__padding-block);font-family:var(--uxa-tab__font-family, var(--uxa-typography__font-family));font-weight:var(--uxa-tab__font-weight);font-size:var(--uxa-tab__font-size);color:var(--uxa-tab__color);text-transform:var(--uxa-tab__text-transform);cursor:var(--uxa-tab__cursor);box-shadow:var(--uxa-tab__box-shadow);min-width:var(--uxa-tab__min-width);max-width:var(--uxa-tab__max-width);white-space:var(--uxa-tab__white-space);border-radius:var(--uxa-tab__border-radius);outline:var(--uxa-tab__outline, none);outline-offset:var(--uxa-tab__outline-offset, 0);--uxa-icon__size: var(--uxa-tab-icon__size)}.uxa-tab[_ngcontent-%COMP%]:hover:not(.uxa-disabled){--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--hover);--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--hover);--uxa-tab-indicator__background-color: var(--uxa-tab-indicator__background-color--hover)}.uxa-tab[_ngcontent-%COMP%]:active:not(.uxa-disabled){--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--active);--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--active);--uxa-tab-indicator__background-color: var(--uxa-tab-indicator__background-color--active)}.uxa-tab.uxa-focused[_ngcontent-%COMP%]{--uxa-tab__box-shadow: var(--uxa-tab__box-shadow--focused)}.uxa-tab.uxa-selected[_ngcontent-%COMP%]{--uxa-tab__color: var(--uxa-tab__color--selected);--uxa-tab__font-weight: var(--uxa-tab__font-weight--selected);--uxa-tab-indicator__background-color: var(--uxa-tab-indicator__background-color--selected);--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--selected)}.uxa-tab.uxa-selected[_ngcontent-%COMP%]:hover{--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--selected-hover);--uxa-tab-indicator__background-color: var( --uxa-tab-indicator__background-color--selected-hover )}.uxa-tab.uxa-selected[_ngcontent-%COMP%]:active{--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--selected-active);--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--selected-active);--uxa-tab-indicator__background-color: var( --uxa-tab-indicator__background-color--selected-active )}.uxa-tab.uxa-disabled[_ngcontent-%COMP%]{--uxa-tab__color: var(--uxa-tab__color--disabled);--uxa-tab__cursor: var(--uxa-tab__cursor--disabled)}.uxa-tab-indicator[_ngcontent-%COMP%], .uxa-tab-indicator[_nghost-%COMP%]{position:absolute;inset-inline-start:var(--uxa-tab-indicator__inset-inline-start);bottom:0;width:var(--uxa-tab-indicator__width);margin-inline:auto;margin-block:0;height:var(--uxa-tab-indicator__height);background-color:var(--uxa-tab-indicator__background-color);transition:var(--uxa-tab-indicator__transition);top:var(--uxa-tab-indicator__top);transform:var(--uxa-tab-indicator__translate);border-radius:var(--uxa-tab-indicator__border-radius)}.uxa-tab-panel[_ngcontent-%COMP%]{display:none;overflow:auto;font-family:var(--uxa-typography-body__font-family, var(--uxa-typography__font-family));font-weight:var(--uxa-typography-body__font-weight);font-size:var(--uxa-typography-body__font-size);text-transform:var(--uxa-typography-body__text-transform);color:var(--uxa-typography-body__color);line-height:var(--uxa-typography-body__line-height)}.uxa-tab-panel[_ngcontent-%COMP%]:focus{outline:none}.uxa-tab-panel.uxa-selected[_ngcontent-%COMP%]{display:block;flex:1}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaTabGroupComponent, [{
    type: Component,
    args: [{
      selector: 'uxa-tab-group',
      changeDetection: ChangeDetectionStrategy.OnPush,
      inputs: ['variant'],
      host: {
        '[class.uxa-tab-group]': 'true',
        '[class.uxa-vertical]': 'orientation === "vertical"',
        '[class.uxa-small]': 'size === "small"',
        '[class.uxa-medium]': 'size === "medium"',
        '[attr.aria-label]': 'null'
      },
      standalone: true,
      imports: [UxaTabButtonComponent, NgTemplateOutlet],
      template: "<div class=\"uxa-tab-list\" role=\"tablist\" [attr.aria-orientation]=\"orientation\">\n  @for (tab of _tabs; track trackByTab(index, tab); let index = $index) {\n    <button\n      type=\"button\"\n      uxa-tab-button\n      [id]=\"tab.id\"\n      [active]=\"tab.active\"\n      [disabled]=\"tab.disabled\"\n      (keydown)=\"_onKeydown($event)\"\n      (selected)=\"select(index)\"\n      [attr.aria-label]=\"tab.ariaLabel\"\n      [attr.aria-labelledby]=\"tab.ariaLabelledby\"\n    >\n      @if (!tab._customLabelRef) {\n        {{ tab.label }}\n      } @else {\n        <ng-template [ngTemplateOutlet]=\"tab._customLabelRef\" />\n      }\n\n      <div class=\"uxa-tab-indicator\" role=\"presentation\"></div>\n    </button>\n  }\n</div>\n@for (tab of _tabs; track trackByTab(index, tab); let index = $index) {\n  <div role=\"tabpanel\" class=\"uxa-tab-panel\" [class.uxa-selected]=\"index === activeIndex\">\n    <ng-container [ngTemplateOutlet]=\"tab.content\" />\n  </div>\n}\n",
      styles: [".uxa-tab-group,:host.uxa-tab-group{display:flex;flex-direction:column;column-gap:var(--uxa-tab-group__column-gap);height:100%}.uxa-tab-group.uxa-secondary,:host.uxa-tab-group.uxa-secondary{--uxa-tab-indicator__background-color: var(--uxa-tab-indicator__background-color--secondary);--uxa-tab-indicator__background-color--hover: var( --uxa-tab-indicator__background-color--secondary-hover );--uxa-tab-indicator__background-color--selected: var( --uxa-tab-indicator__background-color--secondary-selected );--uxa-tab-indicator__background-color--selected-hover: var( --uxa-tab-indicator__background-color--secondary-selected-hover );--uxa-tab-indicator__background-color--active: var( --uxa-tab-indicator__background-color--secondary-active );--uxa-tab-indicator__background-color--selected-active: var( --uxa-tab-indicator__background-color--secondary-selected-active )}.uxa-tab-group.uxa-small,:host.uxa-tab-group.uxa-small{--uxa-tab-list__height: var(--uxa-tab-list__height--small);--uxa-tab__font-size: var(--uxa-tab__font-size--small)}.uxa-tab-group.uxa-medium,:host.uxa-tab-group.uxa-medium{--uxa-tab-list__height: var(--uxa-tab-list__height--medium);--uxa-tab__font-size: var(--uxa-tab__font-size--medium)}.uxa-tab-group.uxa-vertical,:host.uxa-tab-group.uxa-vertical{flex-direction:row;--uxa-tab__height: var(--uxa-tab__height--vertical);--uxa-tab__padding-inline: .75rem;--uxa-tab__padding-block: 0;--uxa-tab__justify-content: flex-start;--uxa-tab-list__flex-direction: column;--uxa-tab-list__height: auto;--uxa-tab-list__border-bottom-width: var(--uxa-tab-list__border-bottom-width--vertical);--uxa-tab-list__border-inline-end-width: var(--uxa-tab-list__border-inline-end-width--vertical);--uxa-tab-indicator__inset-inline-start: 0;--uxa-tab-indicator__width--selected: 3px;--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--vertical);--uxa-tab-indicator__transition: height .24s ease-out;--uxa-tab-indicator__translate: translate(0, -50%);--uxa-tab-indicator__top: 50%;--uxa-tab-indicator__border-radius: var(--uxa-tab-indicator__border-radius--vertical);--uxa-tab-indicator__height--hover: var(--uxa-tab-indicator__height--vertical-hover);--uxa-tab-indicator__width--hover: var(--uxa-tab-indicator__width--vertical-hover);--uxa-tab-indicator__width--selected-hover: var( --uxa-tab-indicator__width--vertical-selected-hover );--uxa-tab-indicator__height--active: var(--uxa-tab-indicator__height--vertical-active);--uxa-tab-indicator__width--active: var(--uxa-tab-indicator__width--vertical-active);--uxa-tab-indicator__width--selected-active: var( --uxa-tab-indicator__width--vertical-selected-active );--uxa-tab-indicator__height--selected-active: var( --uxa-tab-indicator__height--vertical-selected-active )}.uxa-tab-group.uxa-vertical .uxa-tab.uxa-selected,:host.uxa-tab-group.uxa-vertical .uxa-tab.uxa-selected{--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--vertical-selected)}.uxa-tab-list{flex:none;display:flex;flex-direction:var(--uxa-tab-list__flex-direction, row);column-gap:var(--uxa-tab-list__column-gap);height:var(--uxa-tab-list__height);border-top-width:var(--uxa-tab-list__border-top-width);border-bottom-width:var(--uxa-tab-list__border-bottom-width);border-inline-start-width:var(--uxa-tab-list__border-inline-start-width);border-inline-end-width:var(--uxa-tab-list__border-inline-end-width);border-style:var(--uxa-tab-list__border-style);border-color:var(--uxa-tab-list__border-color)}.uxa-tab{position:relative;display:inline-flex;justify-content:var(--uxa-tab__justify-content);align-items:center;height:var(--uxa-tab__height);text-decoration:none;flex:none;background-color:var(--uxa-tab__background-color);margin-inline:var(--uxa-tab__margin-inline);border:var(--uxa-tab__border);padding-inline:var(--uxa-tab__padding-inline);padding-block:var(--uxa-tab__padding-block);font-family:var(--uxa-tab__font-family, var(--uxa-typography__font-family));font-weight:var(--uxa-tab__font-weight);font-size:var(--uxa-tab__font-size);color:var(--uxa-tab__color);text-transform:var(--uxa-tab__text-transform);cursor:var(--uxa-tab__cursor);box-shadow:var(--uxa-tab__box-shadow);min-width:var(--uxa-tab__min-width);max-width:var(--uxa-tab__max-width);white-space:var(--uxa-tab__white-space);border-radius:var(--uxa-tab__border-radius);outline:var(--uxa-tab__outline, none);outline-offset:var(--uxa-tab__outline-offset, 0);--uxa-icon__size: var(--uxa-tab-icon__size)}.uxa-tab:hover:not(.uxa-disabled){--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--hover);--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--hover);--uxa-tab-indicator__background-color: var(--uxa-tab-indicator__background-color--hover)}.uxa-tab:active:not(.uxa-disabled){--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--active);--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--active);--uxa-tab-indicator__background-color: var(--uxa-tab-indicator__background-color--active)}.uxa-tab.uxa-focused{--uxa-tab__box-shadow: var(--uxa-tab__box-shadow--focused)}.uxa-tab.uxa-selected{--uxa-tab__color: var(--uxa-tab__color--selected);--uxa-tab__font-weight: var(--uxa-tab__font-weight--selected);--uxa-tab-indicator__background-color: var(--uxa-tab-indicator__background-color--selected);--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--selected)}.uxa-tab.uxa-selected:hover{--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--selected-hover);--uxa-tab-indicator__background-color: var( --uxa-tab-indicator__background-color--selected-hover )}.uxa-tab.uxa-selected:active{--uxa-tab-indicator__height: var(--uxa-tab-indicator__height--selected-active);--uxa-tab-indicator__width: var(--uxa-tab-indicator__width--selected-active);--uxa-tab-indicator__background-color: var( --uxa-tab-indicator__background-color--selected-active )}.uxa-tab.uxa-disabled{--uxa-tab__color: var(--uxa-tab__color--disabled);--uxa-tab__cursor: var(--uxa-tab__cursor--disabled)}.uxa-tab-indicator,:host.uxa-tab-indicator{position:absolute;inset-inline-start:var(--uxa-tab-indicator__inset-inline-start);bottom:0;width:var(--uxa-tab-indicator__width);margin-inline:auto;margin-block:0;height:var(--uxa-tab-indicator__height);background-color:var(--uxa-tab-indicator__background-color);transition:var(--uxa-tab-indicator__transition);top:var(--uxa-tab-indicator__top);transform:var(--uxa-tab-indicator__translate);border-radius:var(--uxa-tab-indicator__border-radius)}.uxa-tab-panel{display:none;overflow:auto;font-family:var(--uxa-typography-body__font-family, var(--uxa-typography__font-family));font-weight:var(--uxa-typography-body__font-weight);font-size:var(--uxa-typography-body__font-size);text-transform:var(--uxa-typography-body__text-transform);color:var(--uxa-typography-body__color);line-height:var(--uxa-typography-body__line-height)}.uxa-tab-panel:focus{outline:none}.uxa-tab-panel.uxa-selected{display:block;flex:1}\n"]
    }]
  }], null, {
    size: [{
      type: Input
    }],
    activeIndex: [{
      type: Input,
      args: [{
        transform: numberAttribute
      }]
    }],
    activeId: [{
      type: Input
    }],
    orientation: [{
      type: Input
    }],
    ariaLabel: [{
      type: Input,
      args: ['aria-label']
    }],
    activeIndexChange: [{
      type: Output
    }],
    activeIdChange: [{
      type: Output
    }],
    _tabs: [{
      type: ContentChildren,
      args: [UxaTabComponent]
    }],
    _buttons: [{
      type: ViewChildren,
      args: [UxaTabButtonComponent]
    }]
  });
})();
class UxaTabModule {
  static {
    this.ɵfac = function UxaTabModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || UxaTabModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: UxaTabModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, UxaA11yModule]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(UxaTabModule, [{
    type: NgModule,
    args: [{
      imports: [CommonModule, UxaA11yModule, UxaTabComponent, UxaTabGroupComponent, UxaTabButtonComponent, UxaTabLabelDirective],
      exports: [UxaTabComponent, UxaTabGroupComponent, UxaTabLabelDirective]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { UxaTabComponent, UxaTabGroupComponent, UxaTabLabelDirective, UxaTabModule };
